import { createContext, type ReactNode, useContext, useMemo } from 'react';
import { useActiveSession } from '../api/active/sessionProvider';
import { createCustomerApi } from './customerApi';

export type CustomerPageApi = ReturnType<typeof createCustomerApi>;

export interface CustomerPageIdentity {
  id: string;
  primaryDepartmentId: string;
}

export interface CustomerPageRuntime {
  api: CustomerPageApi;
  identity: CustomerPageIdentity;
  canRead?: boolean;
  canManage: boolean;
  recoverUnauthorized(): Promise<void>;
}

const CustomerPageRuntimeContext = createContext<CustomerPageRuntime | null>(
  null,
);

export function CustomerPageRuntimeProvider({
  children,
  value,
}: {
  children: ReactNode;
  value: CustomerPageRuntime;
}) {
  return (
    <CustomerPageRuntimeContext.Provider value={value}>
      {children}
    </CustomerPageRuntimeContext.Provider>
  );
}

export function ActiveCustomerPageRuntimeProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { state, transport, refresh } = useActiveSession();
  const ready = state.status === 'ready' ? state : null;
  const value = useMemo<CustomerPageRuntime>(
    () => ({
      api: createCustomerApi(transport, ready?.csrfToken ?? null),
      identity: {
        id: ready?.user.id ?? '',
        primaryDepartmentId: ready?.user.primaryDepartmentId ?? '',
      },
      canRead: ready?.user.permissions.includes('customer.read') ?? false,
      canManage: ready?.user.permissions.includes('customer.manage') ?? false,
      recoverUnauthorized: refresh,
    }),
    [ready, refresh, transport],
  );
  if (!ready) return null;
  return (
    <CustomerPageRuntimeContext.Provider value={value}>
      {children}
    </CustomerPageRuntimeContext.Provider>
  );
}

export function useCustomerPageRuntime(): CustomerPageRuntime {
  const value = useContext(CustomerPageRuntimeContext);
  if (!value) throw new Error('CustomerPageRuntimeProvider is required');
  return value;
}

export function useOptionalCustomerPageRuntime(): CustomerPageRuntime | null {
  return useContext(CustomerPageRuntimeContext);
}
